import { Fragment } from 'react'

import {
  connect,
  DataItem,
  getDataClass,
  load,
  navigateTo,
  provideComponent,
  urlForDataItem,
  useDataScope,
} from 'scrivito'

import { DXPSearchResultsWidget } from './DXPSearchResultsTableWidgetClass'
import { SearchPageError } from './SearchPageError'
import { SearchPageNoResults } from './SearchPageNoResults'
import { Column } from '@/Atomic/Templates/Table'
import { DisplayCountIndicator } from '@/Components/DisplayCountIndicator'
import { EditorNote } from '@/Components/EditorNote'
import { EntityTable } from '@/Components/EntityTable/EntityTable'
import { CellRendererParams } from '@/Components/EntityTable/types'
import { DATA_SCOPE_ERROR, ErrorVariant } from '@/Components/error/constants'
import { ErrorPage } from '@/Components/error/Errors'
import { Loading } from '@/Components/Loading'
import { showToast } from '@/Components/Toast'
import { ToastMessageType } from '@/Components/Toast/const'
import { DataClassName } from '@/Data/const'
import { SchemaAttributes } from '@/Data/types'
import {
  getEntityIconElement,
  getEntityIconElementByEntity,
  getSchemaName,
} from '@/utils/dataClassMap'
import { ensureString } from '@/utils/ensureString'
import { getDataItemPromise } from '@/utils/getDataItemPromise'
import { getDataItems } from '@/utils/getDataItems'
import { getSchema } from '@/utils/getSchema'
import { getTableColumns } from '@/utils/getTableColumns'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'

provideComponent(
  DXPSearchResultsWidget,
  () => {
    const dataScope = useDataScope()

    const { dataItems, error: dataError } = getDataItems(dataScope)
    const { schema, error: schemaError } = getSchema(
      getSchemaName(DataClassName.GLOBAL_RESULT),
    )

    const schemaAttributes = getLocalizedSchemaAttributes(
      schema?.get('attributes') as SchemaAttributes,
    )

    const tableColumns = getTableColumns(tableCols, schemaAttributes)

    if (dataError === DATA_SCOPE_ERROR) {
      return <EditorNote>No Data Scope is defined</EditorNote>
    }

    if (dataError) return <SearchPageError />

    if (!schema || schemaError) {
      return <ErrorPage variant={ErrorVariant.InternalServerError} />
    }

    if (dataItems.length === 0) return <SearchPageNoResults />

    return (
      <>
        <div className="row">
          <div className="col-sm-12">
            <DisplayCountIndicator />
            <EntityTable
              tableColumns={tableColumns}
              dataItems={dataItems}
              schema={schema}
              onNavigateTo={onNavigateTo}
              onOpenInNewTab={onOpenInNewTab}
              omitSidebar
            />
          </div>
        </div>
      </>
    )
  },
  { loading: Loading },
)

const navigateToResult = async (dataItem: DataItem, newTab = false) => {
  const dataClassName = ensureString(dataItem.get('entity'))
  const targetDataItem = await getDataItemPromise(dataClassName, dataItem.id())
  if (targetDataItem) {
    if (newTab) {
      load(() => {
        urlForDataItem(targetDataItem)
      }).then(() => {
        const url = urlForDataItem(targetDataItem) || undefined
        window.open(url, '_blank')
      })
    } else {
      navigateTo(targetDataItem)
    }
  } else {
    showToast(
      ToastMessageType.ERROR,
      'Error opening search result',
      'Data class or item not found',
    )
  }
}

const onNavigateTo = async (dataItem: DataItem) => {
  navigateToResult(dataItem)
}

const onOpenInNewTab = async (dataItem: DataItem) => {
  navigateToResult(dataItem, true)
}

const iconConfig = { avatarSize: 'lg' as const }

const SearchResultIconCell = connect(function SearchResultIconCell({
  dataItem,
}: CellRendererParams) {
  const dataClassName = ensureString(dataItem.get('entity'))
  const targetDataItem = getDataClass(dataClassName)?.get(dataItem.id()) ?? null

  if (targetDataItem) {
    return getEntityIconElement(targetDataItem, iconConfig)
  }

  return getEntityIconElementByEntity(dataItem, iconConfig)
})

const getSnippet = ({ dataItem }: CellRendererParams) => {
  const snippet = ensureString(dataItem.get('snippet'))
  const matches = dataItem.get('matches') as number[][] | undefined

  if (!matches?.length) {
    return <>{snippet}</>
  }

  return (
    <>
      {matches?.map((match, index) => {
        const previousEnd = matches?.[index - 1]?.[1] || 0
        const begin = match?.[0] || 0
        const end = match?.[1] || 0
        const lastMatch = index + 1 === matches?.length
        return (
          <Fragment key={index}>
            {snippet.slice(previousEnd, begin)}
            <mark>{snippet.slice(begin, end)}</mark>
            {lastMatch ? snippet.slice(end) : null}
          </Fragment>
        )
      })}
    </>
  )
}

const tableCols = [
  {
    accessor: 'icon',
    title: '',
    variant: Column.WIDTH_80PX,
    cell: SearchResultIconCell,
  },
  {
    accessor: 'entity',
    title: 'Entity',
    variant: Column.WIDTH_80PX,
  },
  {
    accessor: 'title',
    title: 'Title',
    main: true,
    variant: Column.MAIN,
  },
  {
    accessor: 'snippet',
    title: 'Snippet',
    variant: Column.FLEX,
    cell: getSnippet,
  },
  {
    accessor: '',
    title: '',
    variant: Column.ACTIONS,
  },
]
